import type { LabModule } from "./types";

/** Each lab owns its own private state shape, so the registry stays state-agnostic. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyLab = LabModule<any>;

export type LabMeta = {
  id: string;
  title: string;
  chapter: string;
  group: "Mechanics" | "Waves & Thermal" | "Electromagnetism" | "Optics" | "Modern Physics";
  blurb: string;
  apparatus: string;
  load: () => Promise<{ default: AnyLab }>;
};

/** Every experiment is dynamically imported so the hub stays tiny. */
export const LABS: LabMeta[] = [
  {
    id: "vernier-measurement",
    title: "Vernier & Screw Gauge Bench",
    chapter: "Units & Measurement",
    group: "Mechanics",
    blurb: "Slide the jaws over a real object and read the main + vernier scale yourself.",
    apparatus: "Vernier caliper, screw gauge, test blocks",
    load: () => import("./labs/vernier-measurement"),
  },
  {
    id: "projectile-cannon",
    title: "Projectile Cannon Range Bench",
    chapter: "Kinematics",
    group: "Mechanics",
    blurb: "Aim the barrel by dragging it, fire, and hit the movable target.",
    apparatus: "Spring cannon, protractor, sliding target",
    load: () => import("./labs/projectile-cannon"),
  },
  {
    id: "newton-pulley",
    title: "Atwood Pulley & Force Table",
    chapter: "Laws of Motion",
    group: "Mechanics",
    blurb: "Hang masses on both sides and watch the real acceleration build.",
    apparatus: "Pulley, mass hangers, spring balance",
    load: () => import("./labs/newton-pulley"),
  },
  {
    id: "friction-incline",
    title: "Friction Incline Bench",
    chapter: "Friction",
    group: "Mechanics",
    blurb: "Tilt the plank by dragging it until the block breaks free.",
    apparatus: "Adjustable incline, block, surface swatches",
    load: () => import("./labs/friction-incline"),
  },
  {
    id: "work-energy-ramp",
    title: "Energy Ramp & Loop",
    chapter: "Work, Energy & Power",
    group: "Mechanics",
    blurb: "Drag the cart up the ramp, release, and track KE ↔ PE exchange.",
    apparatus: "Ramp, cart, energy bars",
    load: () => import("./labs/work-energy-ramp"),
  },
  {
    id: "collision-table",
    title: "Collision Air Table",
    chapter: "Centre of Mass & Collisions",
    group: "Mechanics",
    blurb: "Flick two pucks together and change how elastic the impact is.",
    apparatus: "Air table, two pucks, momentum meter",
    load: () => import("./labs/collision-table"),
  },
  {
    id: "rotational-flywheel",
    title: "Flywheel & Torque Bench",
    chapter: "Rotational Motion",
    group: "Mechanics",
    blurb: "Hang a mass off the axle and time the angular acceleration.",
    apparatus: "Flywheel, axle, hanging mass",
    load: () => import("./labs/rotational-flywheel"),
  },
  {
    id: "gravitation-launch",
    title: "Orbit Launch Console",
    chapter: "Gravitation",
    group: "Mechanics",
    blurb: "Drag the launch vector and find the speed that closes the orbit.",
    apparatus: "Planet, satellite, velocity arrow",
    load: () => import("./labs/gravitation-launch"),
  },
  {
    id: "elasticity-wire",
    title: "Young's Modulus Wire Bench",
    chapter: "Mechanical Properties of Solids",
    group: "Mechanics",
    blurb: "Load the wire and watch it stretch through elastic and plastic zones.",
    apparatus: "Searle's apparatus, weights, micrometer",
    load: () => import("./labs/elasticity-wire"),
  },
  {
    id: "fluids-bench",
    title: "Bernoulli & Buoyancy Bench",
    chapter: "Mechanical Properties of Fluids",
    group: "Mechanics",
    blurb: "Drag the block into water and open a Venturi tube flow.",
    apparatus: "Water tank, Venturi tube, manometers",
    load: () => import("./labs/fluids-bench"),
  },
  {
    id: "calorimetry-bench",
    title: "Calorimetry & Heat Transfer",
    chapter: "Thermal Properties of Matter",
    group: "Waves & Thermal",
    blurb: "Drop a hot metal block into water and watch equilibrium arrive.",
    apparatus: "Calorimeter, thermometer, hot block",
    load: () => import("./labs/calorimetry-bench"),
  },
  {
    id: "kinetic-theory-box",
    title: "Kinetic Theory Gas Box",
    chapter: "Kinetic Theory & Thermodynamics",
    group: "Waves & Thermal",
    blurb: "Drag the piston to compress real colliding particles.",
    apparatus: "Particle chamber, piston, pressure gauge",
    load: () => import("./labs/kinetic-theory-box"),
  },
  {
    id: "pendulum-shm",
    title: "Pendulum & Spring SHM Bench",
    chapter: "Oscillations",
    group: "Waves & Thermal",
    blurb: "Pull the bob aside, release, and time twenty oscillations.",
    apparatus: "Pendulum, spring-mass, stopwatch",
    load: () => import("./labs/pendulum-shm"),
  },
  {
    id: "waves-string",
    title: "Standing Wave String Bench",
    chapter: "Waves",
    group: "Waves & Thermal",
    blurb: "Pluck the string and tune tension until a clean harmonic locks in.",
    apparatus: "Sonometer, tuning fork, tension weights",
    load: () => import("./labs/waves-string"),
  },
  {
    id: "electrostatics-field",
    title: "Charge & Field Mapping Table",
    chapter: "Electrostatics",
    group: "Electromagnetism",
    blurb: "Drag charges around the table and probe the field with a test charge.",
    apparatus: "Charge pucks, field probe, equipotential grid",
    load: () => import("./labs/electrostatics-field"),
  },
  {
    id: "circuit-builder",
    title: "Breadboard Circuit Builder",
    chapter: "Current Electricity",
    group: "Electromagnetism",
    blurb: "Wire resistors in series or parallel and read the meters.",
    apparatus: "Cell, resistors, ammeter, voltmeter",
    load: () => import("./labs/circuit-builder"),
  },
  {
    id: "magnetic-field-bench",
    title: "Magnetic Force & Field Bench",
    chapter: "Magnetism & Moving Charges",
    group: "Electromagnetism",
    blurb: "Fire a charge into the field region and bend its path.",
    apparatus: "Field region, charge gun, compass",
    load: () => import("./labs/magnetic-field-bench"),
  },
  {
    id: "induction-coil",
    title: "Faraday Induction Bench",
    chapter: "Electromagnetic Induction & AC",
    group: "Electromagnetism",
    blurb: "Drag the magnet through the coil and watch the galvanometer swing.",
    apparatus: "Bar magnet, coil, galvanometer",
    load: () => import("./labs/induction-coil"),
  },
  {
    id: "optics-bench",
    title: "Ray Optics Bench",
    chapter: "Ray Optics",
    group: "Optics",
    blurb: "Slide the object and lens along the rail and trace real rays.",
    apparatus: "Optical rail, lens, screen, candle",
    load: () => import("./labs/optics-bench"),
  },
  {
    id: "wave-optics-ydse",
    title: "Young's Double Slit Bench",
    chapter: "Wave Optics",
    group: "Optics",
    blurb: "Drag the screen back and watch the fringe width grow.",
    apparatus: "Laser, double slit, screen, travelling microscope",
    load: () => import("./labs/wave-optics-bench"),
  },
  {
    id: "photoelectric-bench",
    title: "Photoelectric Effect Bench",
    chapter: "Dual Nature & Modern Physics",
    group: "Modern Physics",
    blurb: "Change the lamp colour and find the stopping potential.",
    apparatus: "Photocell, monochromator, retarding supply",
    load: () => import("./labs/photoelectric-bench"),
  },
  {
    id: "semiconductor-diode",
    title: "Diode Characteristics Bench",
    chapter: "Semiconductor Devices",
    group: "Modern Physics",
    blurb: "Sweep the bias voltage and trace the I–V curve point by point.",
    apparatus: "Diode, variable supply, ammeter",
    load: () => import("./labs/semiconductor-diode"),
  },
];

export const LAB_GROUPS = [
  "Mechanics",
  "Waves & Thermal",
  "Electromagnetism",
  "Optics",
  "Modern Physics",
] as const;

export const findLab = (id: string) => LABS.find((l) => l.id === id);